/** Sample help center content for the support widget. Replace with articles from your help center or CMS. */
export type Article = { id: string; title: string; category: string; minutes: number; updated: string; author: Person; summary: string; intro: string; steps: string[]; note: string; keywords: string };
export type Person = { name: string; src: string };

export const TEAM = {
  ava: { name: "Ava Mitchell", src: "/panel-assets/people/ava-mitchell.jpg" },
  marcus: { name: "Marcus Johnson", src: "/panel-assets/people/marcus-johnson.jpg" },
  sofia: { name: "Sofia Ramirez", src: "/panel-assets/people/sofia-ramirez.jpg" },
  emma: { name: "Emma Collins", src: "/panel-assets/people/emma-collins.jpg" },
  jasmine: { name: "Jasmine Brooks", src: "/panel-assets/people/jasmine-brooks.jpg" },
} satisfies Record<string, Person>;

export const ARTICLES: Article[] = [
  {
    "id": "booking",
    "title": "Request a ride",
    "category": "Reservations",
    "minutes": 2,
    "updated": "Oct 5, 2026",
    "summary": "Share your itinerary and vehicle requirements.",
    "intro": "The Danaro team reviews your itinerary and confirms availability before a reservation is finalized.",
    "steps": [
      "Share the travel date, pickup and destination.",
      "Include passenger count, luggage and preferred vehicle.",
      "Review the quote and payment instructions from the team.",
      "Keep your reservation reference for future updates."
    ],
    "note": "Your final confirmation contains the agreed itinerary and terms.",
    "keywords": "book request quote price vehicle limo sedan sprinter reservation"
  },
  {
    "id": "changes",
    "title": "Update your reservation",
    "category": "My Danaro",
    "minutes": 2,
    "updated": "Oct 5, 2026",
    "summary": "Send pickup, passenger or itinerary changes to the team.",
    "intro": "Changes are reviewed against availability and the existing reservation.",
    "steps": [
      "Open the reservation in My Danaro.",
      "Share the details that need to change.",
      "Ask the team to review timing, resources and any price adjustment.",
      "Wait for the updated confirmation."
    ],
    "note": "Sending a request starts the review; the team confirms the resulting itinerary.",
    "keywords": "change update amend pickup time passengers route"
  },
  {
    "id": "airport",
    "title": "Prepare for an airport pickup",
    "category": "Airport transfers",
    "minutes": 2,
    "updated": "Oct 5, 2026",
    "summary": "Have your flight and destination details ready.",
    "intro": "Flight details help the team plan the pickup and coordinate your arrival.",
    "steps": [
      "Share the airport, flight number and arrival date.",
      "Confirm passenger count and luggage needs.",
      "Review the meeting instructions for your reservation.",
      "Message the team if your itinerary changes."
    ],
    "note": "Your reservation instructions explain where to meet your chauffeur.",
    "keywords": "airport flight ewr jfk lga arrival luggage chauffeur"
  },
  {
    "id": "payments",
    "title": "Find quotes and payment information",
    "category": "Documents",
    "minutes": 2,
    "updated": "Oct 5, 2026",
    "summary": "Review your quote, receipt and outstanding balance.",
    "intro": "Your commercial documents contain the agreed services and applicable payment instructions.",
    "steps": [
      "Open the relevant document in My Danaro.",
      "Review its amount, due date and payment instructions.",
      "Use your reservation reference when contacting support.",
      "Ask for help if a receipt or balance needs review."
    ],
    "note": "The team can help locate the document associated with your reservation.",
    "keywords": "payment deposit invoice quote receipt balance refund"
  },
  {
    "id": "human",
    "title": "Talk to a team member",
    "category": "Support",
    "minutes": 1,
    "updated": "Oct 5, 2026",
    "summary": "Continue an AI conversation with human support.",
    "intro": "AI assistance and the Danaro team share the same conversation history.",
    "steps": [
      "Open chat and describe what you need.",
      "Choose Talk to a person when you need human assistance.",
      "Keep the conversation open or return to read new replies."
    ],
    "note": "Your messages stay in the conversation when a team member takes over.",
    "keywords": "support human person agent chat ai help"
  }
].map(article => ({ ...article, author: { name: "Danaro Support", src: "/panel-assets/shell/danaro-logo.svg" } }));

export const POPULAR = ["booking", "changes", "airport", "payments", "human"];
export const SUGGESTIONS = ["I need an airport transfer", "I would like to speak to a person"];

const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
export const tokensOf = (query: string) => Array.from(new Set(query.toLowerCase().split(/\s+/).filter(Boolean))).slice(0, 6);
export const matcherOf = (tokens: string[]) => tokens.length ? new RegExp(`(${tokens.map(escape).join("|")})`, "gi") : null;

/** Every word has to appear somewhere; title hits rank above summary hits, then the original order breaks ties. */
export function searchArticles(query: string) {
  const tokens = tokensOf(query);
  if (!tokens.length) return POPULAR.map(id => ARTICLES.find(article => article.id === id)!);
  return ARTICLES
    .map((article, index) => {
      const title = article.title.toLowerCase(), summary = article.summary.toLowerCase();
      const haystack = `${title} ${summary} ${article.category.toLowerCase()} ${article.keywords}`;
      if (!tokens.every(token => haystack.includes(token))) return null;
      const score = tokens.reduce((total, token) => total + (title.includes(token) ? 3 : 0) + (summary.includes(token) ? 1 : 0), 0);
      return { article, score, index };
    })
    .filter(hit => hit !== null)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(hit => hit.article);
}

/** Canned replies keyed on what the person wrote. Every reply in the preview is simulated. */
export function replyTo(text: string) {
  const value = text.toLowerCase();
  if (/export|arrive|csv|pdf|report|churn/.test(value)) return "Found it. The Churned accounts export paused on Sep 21 because the Postgres password changed. Update it under Data sources and I’ll rerun the export for you.";
  if (/seat|plan|invite|billing|upgrade|invoice/.test(value)) return "You’re using 12 of 15 seats on Growth, so two more fit without changing plans. Invite them from Settings, then Members.";
  if (/sso|okta|saml|login|sign in/.test(value)) return "Happy to help with SSO. Send me the error from Okta’s sign-in log and I’ll check the SAML settings on our side.";
  if (/thank|great|perfect|works|worked/.test(value)) return "Anytime, Jordan. I’ll keep this thread open in case anything else comes up.";
  return "Thanks, Jordan. I’m looking at your workspace now and will reply here within a few minutes.";
}
export const greetingFor = (article?: Article) => article ? `Hi Jordan, I’m Ava. I have “${article.title}” open too, so we can pick up from there. What’s going on?` : "Hi Jordan, I’m Ava from Harbor support. What can I help with today?";
export const FOLLOW_UP = "No rush, Jordan. I’ll keep this conversation open, and my replies will wait for you here.";
